import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import CryptoJS from 'crypto-js';
import {
  WidgetInstance,
  WidgetSettings,
  WidgetSizeVariant,
  ContainerInstance,
  ContainerChild,
} from '../components/advanced/dashboard/types';

const SECRET = import.meta.env.VITE_CLIENT_SECRET || 'dev-secret-key';

// Encryption helpers for persistence (same pattern as preferencesStore)
const encryptedStorage = {
  getItem: (name: string): string | null => {
    const cipher = localStorage.getItem(name);
    if (!cipher) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(cipher, SECRET);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    const cipher = CryptoJS.AES.encrypt(value, SECRET).toString();
    localStorage.setItem(name, cipher);
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
  },
};

// ============================================================================
// Default Widgets Configuration
// ============================================================================

const DEFAULT_WIDGETS: WidgetInstance[] = [
  { instanceId: 'w-default-1', widgetId: 'invoice-stats', order: 0, isCollapsed: false },
  { instanceId: 'w-default-2', widgetId: 'projects-progress', order: 1, isCollapsed: false },
  { instanceId: 'w-default-3', widgetId: 'latest-orders', order: 2, isCollapsed: false },
  { instanceId: 'w-default-4', widgetId: 'todo-list', order: 3, isCollapsed: false },
  { instanceId: 'w-default-5', widgetId: 'people-on-leave', order: 4, isCollapsed: false },
  { instanceId: 'w-default-6', widgetId: 'upcoming-meetings', order: 5, isCollapsed: false },
];

// ============================================================================
// Store Types
// ============================================================================

interface DashboardState {
  /** User's dashboard widgets */
  widgets: WidgetInstance[];
  /** User's dashboard containers */
  containers: ContainerInstance[];
  /** Edit mode flag for drag-drop */
  isEditMode: boolean;
  /** Currently dragged widget ID */
  draggedWidgetId: string | null;
  /** Currently dragged container ID */
  draggedContainerId: string | null;
  /** Widget picker modal open state */
  isPickerOpen: boolean;
  /** Container currently being edited (null = none, string = container instanceId) */
  editingContainerId: string | null;
  /** Widget settings modal state */
  settingsModalWidget: string | null;
  /** Container child settings modal state (containerInstanceId and childInstanceId) */
  settingsModalContainerChild: { containerInstanceId: string; childInstanceId: string } | null;
  /** Widget filters modal state */
  filtersModalWidget: string | null;
  /** Expanded/fullscreen widget */
  expandedWidget: string | null;
  /** Global refresh trigger counter */
  refreshTrigger: number;
}

interface DashboardActions {
  // Widget management
  addWidget: (widgetId: string, customProps?: Record<string, unknown>) => void;
  removeWidget: (instanceId: string) => void;
  reorderWidgets: (fromIndex: number, toIndex: number) => void;

  // Widget state
  toggleWidgetCollapsed: (instanceId: string) => void;
  setWidgetCollapsed: (instanceId: string, isCollapsed: boolean) => void;
  setWidgetFilters: (instanceId: string, filters: Record<string, unknown>) => void;
  setWidgetProps: (instanceId: string, props: Record<string, unknown>) => void;
  setWidgetSettings: (instanceId: string, settings: Partial<WidgetSettings>) => void;
  setWidgetSizeVariant: (instanceId: string, variant: WidgetSizeVariant) => void;
  setWidgetOrder: (instanceId: string, order: number) => void;

  // Container management
  addContainer: (containerId: string) => void;
  removeContainer: (instanceId: string) => void;
  reorderContainers: (fromIndex: number, toIndex: number) => void;
  setContainerSettings: (instanceId: string, settings: Partial<WidgetSettings>) => void;
  setContainerOrder: (instanceId: string, order: number) => void;

  // Container children management
  addWidgetToContainer: (containerInstanceId: string, slotId: string, widgetId: string) => void;
  removeWidgetFromContainer: (containerInstanceId: string, childInstanceId: string) => void;
  updateContainerChildSettings: (
    containerInstanceId: string,
    childInstanceId: string,
    settings: Partial<WidgetSettings>
  ) => void;
  moveWidgetBetweenSlots: (
    containerInstanceId: string,
    childInstanceId: string,
    newSlotId: string
  ) => void;
  moveWidgetToContainer: (
    widgetInstanceId: string,
    containerInstanceId: string,
    slotId: string
  ) => void;

  // Container editing mode
  setEditingContainer: (instanceId: string | null) => void;
  setDraggedContainer: (instanceId: string | null) => void;

  // Edit mode
  setEditMode: (enabled: boolean) => void;
  toggleEditMode: () => void;
  setDraggedWidget: (instanceId: string | null) => void;

  // Widget picker
  openPicker: () => void;
  closePicker: () => void;

  // Settings modal
  openSettingsModal: (instanceId: string) => void;
  closeSettingsModal: () => void;

  // Container child settings modal
  openContainerChildSettingsModal: (containerInstanceId: string, childInstanceId: string) => void;
  closeContainerChildSettingsModal: () => void;

  // Filters modal
  openFiltersModal: (instanceId: string) => void;
  closeFiltersModal: () => void;

  // Expand widget
  expandWidget: (instanceId: string) => void;
  collapseExpandedWidget: () => void;

  // Refresh
  triggerRefresh: () => void;
  refreshWidget: (instanceId: string) => void;

  // Utilities
  resetToDefault: () => void;
  getWidgetByInstanceId: (instanceId: string) => WidgetInstance | undefined;
  getContainerByInstanceId: (instanceId: string) => ContainerInstance | undefined;
}

type DashboardStore = DashboardState & DashboardActions;

// ============================================================================
// Helper Functions
// ============================================================================

function generateInstanceId(prefix = 'w'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function generateContainerInstanceId(): string {
  return generateInstanceId('c');
}

function generateChildInstanceId(): string {
  return generateInstanceId('ch');
}

// ============================================================================
// Dashboard Store
// ============================================================================

export const useDashboardStore = create<DashboardStore>()(
  persist(
    (set, get) => ({
      // Initial state
      widgets: DEFAULT_WIDGETS,
      containers: [],
      isEditMode: false,
      draggedWidgetId: null,
      draggedContainerId: null,
      isPickerOpen: false,
      editingContainerId: null,
      settingsModalWidget: null,
      settingsModalContainerChild: null,
      filtersModalWidget: null,
      expandedWidget: null,
      refreshTrigger: 0,

      // Widget management
      addWidget: (widgetId, customProps) => {
        const instanceId = generateInstanceId();
        const newWidget: WidgetInstance = {
          instanceId,
          widgetId,
          order: get().widgets.length,
          isCollapsed: false,
          customProps,
        };
        set((state) => ({
          widgets: [...state.widgets, newWidget],
          isPickerOpen: false,
        }));
      },

      removeWidget: (instanceId) => {
        set((state) => ({
          widgets: state.widgets
            .filter((w) => w.instanceId !== instanceId)
            .map((w, index) => ({ ...w, order: index })),
        }));
      },

      reorderWidgets: (fromIndex, toIndex) => {
        set((state) => {
          const widgets = [...state.widgets];
          const [moved] = widgets.splice(fromIndex, 1);
          widgets.splice(toIndex, 0, moved);
          return {
            widgets: widgets.map((w, index) => ({ ...w, order: index })),
          };
        });
      },

      // Widget state
      toggleWidgetCollapsed: (instanceId) => {
        set((state) => ({
          widgets: state.widgets.map((w) =>
            w.instanceId === instanceId ? { ...w, isCollapsed: !w.isCollapsed } : w
          ),
        }));
      },

      setWidgetCollapsed: (instanceId, isCollapsed) => {
        set((state) => ({
          widgets: state.widgets.map((w) =>
            w.instanceId === instanceId ? { ...w, isCollapsed } : w
          ),
        }));
      },

      setWidgetFilters: (instanceId, filters) => {
        set((state) => ({
          widgets: state.widgets.map((w) =>
            w.instanceId === instanceId ? { ...w, filters } : w
          ),
        }));
      },

      setWidgetProps: (instanceId, props) => {
        set((state) => ({
          widgets: state.widgets.map((w) =>
            w.instanceId === instanceId
              ? { ...w, customProps: { ...w.customProps, ...props } }
              : w
          ),
        }));
      },

      setWidgetSettings: (instanceId, settings) => {
        set((state) => ({
          widgets: state.widgets.map((w) =>
            w.instanceId === instanceId
              ? { ...w, settings: { ...w.settings, ...settings } }
              : w
          ),
        }));
      },

      setWidgetSizeVariant: (instanceId, variant) => {
        set((state) => ({
          widgets: state.widgets.map((w) =>
            w.instanceId === instanceId
              ? { ...w, settings: { ...w.settings, sizeVariant: variant } }
              : w
          ),
        }));
      },

      setWidgetOrder: (instanceId, order) => {
        set((state) => ({
          widgets: state.widgets.map((w) =>
            w.instanceId === instanceId ? { ...w, order } : w
          ),
        }));
      },

      // Container management
      addContainer: (containerId) => {
        const instanceId = generateContainerInstanceId();
        // Calculate order: after all widgets and existing containers
        const maxWidgetOrder = Math.max(0, ...get().widgets.map((w) => w.order));
        const maxContainerOrder = get().containers.length > 0
          ? Math.max(...get().containers.map((c) => c.order))
          : maxWidgetOrder;
        const newContainer: ContainerInstance = {
          instanceId,
          containerId,
          order: maxContainerOrder + 1,
          children: [],
        };
        set((state) => ({
          containers: [...state.containers, newContainer],
          isPickerOpen: false,
        }));
      },

      removeContainer: (instanceId) => {
        set((state) => ({
          containers: state.containers.filter((c) => c.instanceId !== instanceId),
          editingContainerId:
            state.editingContainerId === instanceId ? null : state.editingContainerId,
        }));
      },

      reorderContainers: (fromIndex, toIndex) => {
        set((state) => {
          const containers = [...state.containers];
          const [moved] = containers.splice(fromIndex, 1);
          containers.splice(toIndex, 0, moved);
          return {
            containers: containers.map((c, index) => ({ ...c, order: index })),
          };
        });
      },

      setContainerSettings: (instanceId, settings) => {
        set((state) => ({
          containers: state.containers.map((c) =>
            c.instanceId === instanceId
              ? { ...c, settings: { ...c.settings, ...settings } }
              : c
          ),
        }));
      },

      setContainerOrder: (instanceId, order) => {
        set((state) => ({
          containers: state.containers.map((c) =>
            c.instanceId === instanceId ? { ...c, order } : c
          ),
        }));
      },

      // Container children management
      addWidgetToContainer: (containerInstanceId, slotId, widgetId) => {
        const childInstanceId = generateChildInstanceId();
        const newChild: ContainerChild = {
          instanceId: childInstanceId,
          widgetId,
          slotId,
        };
        set((state) => ({
          containers: state.containers.map((c) =>
            c.instanceId === containerInstanceId
              ? { ...c, children: [...c.children, newChild] }
              : c
          ),
        }));
      },

      removeWidgetFromContainer: (containerInstanceId, childInstanceId) => {
        set((state) => ({
          containers: state.containers.map((c) =>
            c.instanceId === containerInstanceId
              ? { ...c, children: c.children.filter((ch) => ch.instanceId !== childInstanceId) }
              : c
          ),
        }));
      },

      updateContainerChildSettings: (containerInstanceId, childInstanceId, settings) => {
        set((state) => ({
          containers: state.containers.map((c) =>
            c.instanceId === containerInstanceId
              ? {
                  ...c,
                  children: c.children.map((ch) =>
                    ch.instanceId === childInstanceId
                      ? { ...ch, settings: { ...ch.settings, ...settings } }
                      : ch
                  ),
                }
              : c
          ),
        }));
      },

      moveWidgetBetweenSlots: (containerInstanceId, childInstanceId, newSlotId) => {
        set((state) => ({
          containers: state.containers.map((c) =>
            c.instanceId === containerInstanceId
              ? {
                  ...c,
                  children: c.children.map((ch) =>
                    ch.instanceId === childInstanceId ? { ...ch, slotId: newSlotId } : ch
                  ),
                }
              : c
          ),
        }));
      },

      moveWidgetToContainer: (widgetInstanceId, containerInstanceId, slotId) => {
        const state = get();
        const widget = state.widgets.find((w) => w.instanceId === widgetInstanceId);
        if (!widget) return;

        const childInstanceId = generateChildInstanceId();
        const newChild: ContainerChild = {
          instanceId: childInstanceId,
          widgetId: widget.widgetId,
          slotId,
          settings: widget.settings,
        };

        set((state) => ({
          // Remove widget from dashboard
          widgets: state.widgets
            .filter((w) => w.instanceId !== widgetInstanceId)
            .map((w, index) => ({ ...w, order: index })),
          // Add widget to container
          containers: state.containers.map((c) =>
            c.instanceId === containerInstanceId
              ? { ...c, children: [...c.children, newChild] }
              : c
          ),
        }));
      },

      // Container editing mode
      setEditingContainer: (instanceId) => set({ editingContainerId: instanceId }),
      setDraggedContainer: (instanceId) => set({ draggedContainerId: instanceId }),

      // Edit mode
      setEditMode: (enabled) => set({ isEditMode: enabled }),
      toggleEditMode: () => set((state) => ({ isEditMode: !state.isEditMode })),
      setDraggedWidget: (instanceId) => set({ draggedWidgetId: instanceId }),

      // Widget picker
      openPicker: () => set({ isPickerOpen: true }),
      closePicker: () => set({ isPickerOpen: false }),

      // Settings modal
      openSettingsModal: (instanceId) => set({ settingsModalWidget: instanceId }),
      closeSettingsModal: () => set({ settingsModalWidget: null }),

      // Container child settings modal
      openContainerChildSettingsModal: (containerInstanceId, childInstanceId) =>
        set({ settingsModalContainerChild: { containerInstanceId, childInstanceId } }),
      closeContainerChildSettingsModal: () => set({ settingsModalContainerChild: null }),

      // Filters modal
      openFiltersModal: (instanceId) => set({ filtersModalWidget: instanceId }),
      closeFiltersModal: () => set({ filtersModalWidget: null }),

      // Expand widget
      expandWidget: (instanceId) => set({ expandedWidget: instanceId }),
      collapseExpandedWidget: () => set({ expandedWidget: null }),

      // Refresh
      triggerRefresh: () => set((state) => ({ refreshTrigger: state.refreshTrigger + 1 })),
      refreshWidget: (instanceId) => {
        // Widget-specific refresh handled via refreshTrigger or external callback
        // This is a placeholder for widget-specific refresh logic
        set((state) => ({ refreshTrigger: state.refreshTrigger + 1 }));
      },

      // Utilities
      resetToDefault: () =>
        set({
          widgets: DEFAULT_WIDGETS.map((w) => ({
            ...w,
            instanceId: generateInstanceId(),
          })),
          containers: [],
          isEditMode: false,
          draggedWidgetId: null,
          draggedContainerId: null,
          editingContainerId: null,
        }),

      getWidgetByInstanceId: (instanceId) => {
        return get().widgets.find((w) => w.instanceId === instanceId);
      },

      getContainerByInstanceId: (instanceId) => {
        return get().containers.find((c) => c.instanceId === instanceId);
      },
    }),
    {
      name: 'ccms_dashboard',
      storage: {
        getItem: (name) => {
          const value = encryptedStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name, value) => {
          encryptedStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          encryptedStorage.removeItem(name);
        },
      },
      partialize: (state) => ({
        widgets: state.widgets,
        containers: state.containers,
      } as unknown as DashboardStore),
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectWidgets = (state: DashboardStore) => state.widgets;
export const selectIsEditMode = (state: DashboardStore) => state.isEditMode;
export const selectDraggedWidgetId = (state: DashboardStore) => state.draggedWidgetId;
export const selectIsPickerOpen = (state: DashboardStore) => state.isPickerOpen;
export const selectWidgetCount = (state: DashboardStore) => state.widgets.length;

// Memoized sorted widgets selector to avoid creating new array references
let lastWidgets: WidgetInstance[] | null = null;
let lastSortedWidgets: WidgetInstance[] | null = null;

export const selectSortedWidgets = (state: DashboardStore): WidgetInstance[] => {
  // Only re-sort if the widgets array reference has changed
  if (state.widgets !== lastWidgets) {
    lastWidgets = state.widgets;
    lastSortedWidgets = [...state.widgets].sort((a, b) => a.order - b.order);
  }
  return lastSortedWidgets!;
};

export const selectSettingsModalWidget = (state: DashboardStore) => state.settingsModalWidget;
export const selectSettingsModalContainerChild = (state: DashboardStore) =>
  state.settingsModalContainerChild;
export const selectFiltersModalWidget = (state: DashboardStore) => state.filtersModalWidget;
export const selectExpandedWidget = (state: DashboardStore) => state.expandedWidget;
export const selectRefreshTrigger = (state: DashboardStore) => state.refreshTrigger;

// Container selectors
export const selectContainers = (state: DashboardStore) => state.containers;
export const selectContainerCount = (state: DashboardStore) => state.containers.length;
export const selectEditingContainerId = (state: DashboardStore) => state.editingContainerId;
export const selectDraggedContainerId = (state: DashboardStore) => state.draggedContainerId;

// Memoized sorted containers selector
let lastContainers: ContainerInstance[] | null = null;
let lastSortedContainers: ContainerInstance[] | null = null;

export const selectSortedContainers = (state: DashboardStore): ContainerInstance[] => {
  if (state.containers !== lastContainers) {
    lastContainers = state.containers;
    lastSortedContainers = [...state.containers].sort((a, b) => a.order - b.order);
  }
  return lastSortedContainers!;
};
